/**
 * `MobileNav` — phone chrome: fixed top bar + slide-over drawer + bottom tabs.
 *
 * Rendered only below Tailwind `md` (768px) — every root element carries
 * `md:hidden`. The desktop `<Sidebar>` handles ≥768px. Both consume the
 * shared `NAV_SECTIONS` registry so the link sets never drift.
 *
 * Pieces:
 *   - Top bar (h-12, fixed): hamburger + ReefApp brand.
 *   - Drawer: slide-over panel (z-50) with every nav section; closes on
 *     backdrop tap, Escape, or link tap.
 *   - Bottom tab bar (h-14, fixed): Monitor / Apex / Logs / More.
 *     "More" opens the drawer.
 *
 * `AppShell` adds `pt-12 pb-16` (mobile only) so content never hides
 * under the fixed bars.
 */
import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';

import {
  IconApex,
  IconChart,
  IconClose,
  IconMenu,
  IconMonitor,
  NAV_SECTIONS,
} from './navSections';

interface BottomTab {
  to: string;
  label: string;
  end?: boolean;
  icon: JSX.Element;
}

const BOTTOM_TABS: BottomTab[] = [
  { to: '/', label: 'Monitor', end: true, icon: <IconMonitor /> },
  { to: '/apex', label: 'Apex', icon: <IconApex /> },
  { to: '/logs/parameters', label: 'Logs', icon: <IconChart /> },
];

export function MobileNav() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <>
      {/* ── Top bar ──────────────────────────────────────────────── */}
      <header
        data-testid="mobile-topbar"
        className="md:hidden fixed top-0 inset-x-0 z-40 flex h-12 items-center gap-3 border-b border-surface-stroke bg-bg-elevated backdrop-blur-md px-3"
      >
        <button
          type="button"
          data-testid="mobile-hamburger"
          aria-label="Open navigation menu"
          onClick={openDrawer}
          className="rounded-md p-2 text-text-1 hover:bg-surface-glass active:bg-surface-glass transition"
        >
          <IconMenu />
        </button>
        <span className="text-sm font-semibold tracking-tight text-text-1">
          <span className="text-accent">Reef</span>App
        </span>
      </header>

      {/* ── Slide-over drawer ────────────────────────────────────── */}
      {drawerOpen ? (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close navigation menu"
            data-testid="mobile-drawer-backdrop"
            onClick={closeDrawer}
            className="absolute inset-0 w-full bg-black/60"
          />
          {/* Panel */}
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col bg-bg-elevated border-r border-surface-stroke shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between border-b border-surface-stroke px-4 h-12 shrink-0">
              <span className="text-sm font-semibold tracking-tight text-text-1">
                <span className="text-accent">Reef</span>App
              </span>
              <button
                type="button"
                data-testid="mobile-drawer-close"
                aria-label="Close navigation menu"
                onClick={closeDrawer}
                className="rounded-md p-2 text-text-2 hover:bg-surface-glass transition"
              >
                <IconClose />
              </button>
            </div>
            <nav className="flex-1 py-2" data-testid="mobile-drawer-nav">
              {NAV_SECTIONS.map((section) => (
                <div key={section.title} className="mb-2">
                  <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-3">
                    {section.title}
                  </div>
                  <ul className="flex flex-col gap-0.5 px-2">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          onClick={closeDrawer}
                          data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors',
                              isActive
                                ? 'bg-accent-soft text-accent'
                                : 'text-text-2 hover:bg-surface-glass hover:text-text-1',
                            )
                          }
                        >
                          <span className="shrink-0">{item.icon}</span>
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>
      ) : null}

      {/* ── Bottom tab bar ───────────────────────────────────────── */}
      <nav
        data-testid="mobile-bottomnav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-4 border-t border-surface-stroke bg-bg-elevated backdrop-blur-md h-14 pb-[env(safe-area-inset-bottom)]"
      >
        {BOTTOM_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            data-testid={`mobile-tab-${tab.label.toLowerCase()}`}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
                isActive ? 'text-accent' : 'text-text-3 hover:text-text-1',
              )
            }
          >
            {tab.icon}
            <span>{tab.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          data-testid="mobile-tab-more"
          aria-label="More pages"
          onClick={openDrawer}
          className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-text-3 hover:text-text-1 transition-colors"
        >
          <IconMenu />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
